import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Show, useAuth, useClerk } from "@clerk/react";
import { ShieldAlert } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { WashersAdmin } from "../components/admin/WashersAdmin";
import { ReviewsAdmin } from "../components/admin/ReviewsAdmin";
import { UsersAdmin } from "../components/admin/UsersAdmin";
import axios, { API, authed } from "../lib/api";

const Metric = ({ label, value, testid }) => (
  <div className="rounded-2xl border border-white/[0.07] bg-[#131B26] p-5">
    <p className="text-xs uppercase tracking-[0.2em] text-slate-500">{label}</p>
    <p className="mt-2 font-display text-3xl font-bold" data-testid={testid}>{value}</p>
  </div>
);

const NoAccess = ({ email }) => {
  const { signOut } = useClerk();
  return (
    <main className="mx-auto max-w-md px-4 py-24 text-center" data-testid="admin-no-access">
      <ShieldAlert className="mx-auto text-[#F43F5E]" size={40} />
      <h1 className="mt-4 font-display text-3xl font-bold">Nu ai acces încă</h1>
      <p className="mt-3 text-slate-400">Contul {email} nu este administrator. Roagă un administrator să-ți dea acces din Panoul Manager.</p>
      <button onClick={() => signOut()} data-testid="no-access-signout-btn" className="mt-6 rounded-full border border-white/10 px-5 py-2.5 text-sm hover:border-white/30">Deconectare</button>
    </main>
  );
};

const Dashboard = () => {
  const { getToken } = useAuth();
  const [me, setMe] = useState(null);
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(async () => {
    const r = await axios.get(`${API}/admin/stats`, await authed(getToken));
    setStats(r.data);
  }, [getToken]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/me`, await authed(getToken));
        setMe(r.data);
        if (r.data.role === "admin") loadStats();
      } catch { setMe({ error: true }); }
    })();
  }, [getToken, loadStats]);

  if (!me) return <p className="mx-auto max-w-6xl px-4 py-20 text-slate-500">Se încarcă…</p>;
  if (me.error) return <p className="mx-auto max-w-6xl px-4 py-20 text-slate-400" data-testid="admin-error">Nu am putut încărca panoul. Reîncarcă pagina.</p>;
  if (me.role !== "admin") return <NoAccess email={me.email} />;

  return (
    <main className="mx-auto max-w-6xl px-4 py-10 sm:px-6" data-testid="admin-dashboard">
      <p className="text-xs uppercase tracking-[0.25em] text-[#00F0FF]">Panou Manager</p>
      <h1 className="mt-2 font-display text-4xl font-bold tracking-tight sm:text-5xl">Spălătoria ta</h1>
      {stats && (
        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Metric label="Spălători" value={stats.washers} testid="metric-washers" />
          <Metric label="Recenzii" value={stats.reviews} testid="metric-reviews" />
          <Metric label="Media generală" value={stats.avg || "–"} testid="metric-avg" />
          <Metric label="Ascunse" value={stats.hidden} testid="metric-hidden" />
        </div>
      )}
      <Tabs defaultValue="washers" className="mt-10">
        <TabsList className="bg-[#131B26]">
          <TabsTrigger value="washers" data-testid="admin-tab-washers">Spălători</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="admin-tab-reviews">Recenzii</TabsTrigger>
          <TabsTrigger value="admins" data-testid="admin-tab-admins">Echipă</TabsTrigger>
        </TabsList>
        <TabsContent value="washers" className="mt-6"><WashersAdmin onChange={loadStats} /></TabsContent>
        <TabsContent value="reviews" className="mt-6"><ReviewsAdmin onChange={loadStats} /></TabsContent>
        <TabsContent value="admins" className="mt-6"><UsersAdmin me={me} /></TabsContent>
      </Tabs>
    </main>
  );
};

export default function AdminPage() {
  return (
    <>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
